import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismInput, PrismModal } from '@mylighthouse/prism-ember';
import type { Task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import type { BillingAdmin } from 'frontend-payments/models';
import isValidEmailAddress from 'frontend-utils/utils/is-valid-email-address';

import styles from './modal-add-user.module.css';

interface BillingAdminsModalAddUserSignature {
  Args: {
    billingAdmin: BillingAdmin | null;
    billingAdmins: BillingAdmin[];
    isEditMode?: boolean;
    onCloseModal: () => void;
    onCreateUser: Task<
      void,
      [userData: { email: string; firstName: string; lastName: string }]
    >;
    onEditUser: Task<
      void,
      [userData: { email: string; firstName: string; lastName: string }]
    >;
  };
}

export default class BillingAdminsModalAddUser extends Component<BillingAdminsModalAddUserSignature> {
  @service declare intl: Services['intl'];

  @tracked email = this.args.billingAdmin?.email ?? '';
  @tracked firstName = this.args.billingAdmin?.firstName ?? '';
  @tracked lastName = this.args.billingAdmin?.lastName ?? '';

  get doesBillingAdminExist(): boolean {
    return this.args.billingAdmins.some((admin) => admin.email === this.email);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get emailErrorMessage() {
    if (isEmpty(this.email.trim())) {
      return this.intl.t('general.required');
    }

    if (!isValidEmailAddress(this.email)) {
      return this.intl.t(
        'payments.billing_admins.modal_add_user.invalid_email',
      );
    }

    if (this.doesBillingAdminExist) {
      return this.intl.t('payment.billing_admins.email_already_exists');
    }
    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasEmailError() {
    return (
      this.email.trim() === '' ||
      !isValidEmailAddress(this.email) ||
      this.doesBillingAdminExist
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFormValid() {
    return (
      !isEmpty(this.firstName.trim()) &&
      !isEmpty(this.lastName.trim()) &&
      !isEmpty(this.email.trim()) &&
      isValidEmailAddress(this.email)
    );
  }

  @action
  onInputEmail(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.email = target.value;
  }

  @action
  onInputFirstName(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.firstName = target.value;
  }

  @action
  onInputLastName(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.lastName = target.value;
  }

  @action
  async submitUser(): Promise<void> {
    if (!this.isFormValid) {
      return;
    }

    const userData = {
      email: this.email.trim(),
      firstName: this.firstName.trim(),
      lastName: this.lastName.trim(),
    };

    if (this.args.isEditMode) {
      await this.args.onEditUser.perform(userData);
    } else {
      await this.args.onCreateUser.perform(userData);
    }
  }

  <template>
    <PrismModal
      @onClose={{@onCloseModal}}
      @title={{if
        @isEditMode
        (t "payments.billing_admins.edit_user")
        (t "payments.billing_admins.add_user")
      }}
      data-test-billing-admin-modal
    >
      <:default>
        <div class="modal--small modal--padded flex flex-col">
          <div class="flex gap-400">
            <PrismInput
              @label={{t "payments.billing_admins.table.header.first_name"}}
              @onInput={{this.onInputFirstName}}
              @required={{true}}
              @requiredErrorMessage={{t "general.required"}}
              @value={{this.firstName}}
              data-test-first-name-input
            />
            <PrismInput
              @label={{t "payments.billing_admins.table.header.last_name"}}
              @onInput={{this.onInputLastName}}
              @required={{true}}
              @requiredErrorMessage={{t "general.required"}}
              @value={{this.lastName}}
              data-test-last-name-input
            />
          </div>
          <PrismInput
            @disabled={{@isEditMode}}
            @errorMessage={{this.emailErrorMessage}}
            @hasErrors={{this.hasEmailError}}
            @label={{t "payments.billing_admins.table.header.email"}}
            @onInput={{this.onInputEmail}}
            @removePadding={{true}}
            @required={{true}}
            @requiredErrorMessage={{t "general.required"}}
            @type="email"
            @value={{this.email}}
            data-test-email-input
          />
        </div>
      </:default>
      <:footer>
        <div class={{styles.footer}}>
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{@onCloseModal}}
            data-test-cancel-button
          />
          <PrismButton
            @isDisabled={{not this.isFormValid}}
            @label={{t "general.save"}}
            @onClick={{this.submitUser}}
            data-test-save-button
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
